"use client"
import type React from "react"
import { useState } from "react"
import { Modal, Form, Input, Select, Radio, Typography, Spin, message } from "antd"
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  BookOutlined,
  CalendarOutlined,
  BankOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons"
import { useRouter } from "next/navigation"
import axios from "axios"
import { BASE_URL } from "@/utils/api"
import toast from "react-hot-toast"
const { Title, Paragraph, Text } = Typography
const { Option } = Select
const { TextArea } = Input
interface EnrollmentFormModalProps {
  isOpen: boolean
  onClose: () => void
}

const EnrollmentFormModal: React.FC<EnrollmentFormModalProps> = ({ isOpen, onClose }) => {
  const [form] = Form.useForm()
  const [userEmail, setUserEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const Router = useRouter()

  // OTP verification states
  const [showOtpField, setShowOtpField] = useState(false)
  const [phoneVerified, setPhoneVerified] = useState(false)
  const [sendingOtp, setSendingOtp] = useState(false)
  const [verifyingOtp, setVerifyingOtp] = useState(false)

  // Function to send OTP
  const sendOTP = async () => {
    try {
      const mobile = form.getFieldValue("phone_number")
      if (!mobile) {
        message.error("Please enter your phone number")
        return
      }

      setSendingOtp(true)

      // Send OTP API call
      const response = await axios.post(
        `${BASE_URL}send-otp/`,
        {
          phone_number: `+91${mobile}`,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )

      if (response.data.status === "success") {
        message.success(response.data.message)
        setShowOtpField(true)
      } else {
        message.error(response.data.message || "Error sending OTP")
      }
    } catch (error) {
      console.error("Error sending OTP:", error)
      message.error("Error sending OTP. Please try again.")
    } finally {
      setSendingOtp(false)
    }
  }

  // Function to verify OTP
  const verifyOTP = async () => {
    try {
      const mobile = Number(form.getFieldValue("phone_number"))
      const otp = Number(form.getFieldValue("otp"))

      if (!otp) {
        message.error("Please enter the OTP")
        return
      }

      setVerifyingOtp(true)

      // Verify OTP API call
      const response = await axios.post(
        `${BASE_URL}verify-otp/`,
        {
          phone_number: mobile,
          otp: otp,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      )

      if (response.data.status === "success") {
        message.success(response.data.message)
        setPhoneVerified(true)
      } else {
        message.error(response.data.message || "Invalid OTP")
      }
    } catch (error) {
      console.error("Error verifying OTP:", error)
      message.error("Error verifying OTP. Please try again.")
    } finally {
      setVerifyingOtp(false)
    }
  }

  const handleSubmit = async (values: any) => {
    if (!phoneVerified) {
      message.error("Please verify your phone number first")
      return
    }

    setLoading(true)
    // console.log("Form values:", values)
    try {
      const response = await axios.post(`${BASE_URL}enroll/`, {...values ,lead_source:"enroll form"}, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      // console.log(response)
      toast.success("Form submitted successfully")
      Router.push("/thank-you-offline_course")
    } catch (error) {
      console.log(error)
      toast.error("Failed to submit form")
    } finally {
      setLoading(false)
    }
    setUserEmail(values.email)
  }

  const handleClose = () => {
    form.resetFields()
    setShowOtpField(false)
    setPhoneVerified(false)
    onClose()
  }

  return (
    <Modal
      title={<h2 className="text-orange text-xl">Enrollment Form - W3era Academy</h2>}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width={800}
      bodyStyle={{ maxHeight: "80vh", overflowY: "auto", padding: "20px" }}
    >
      <Spin spinning={loading} tip="Submitting your request...">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark="optional"
          initialValues={{
            employmentStatus: "Employed",
          }}
        >
          {/* Personal Information Section */}
          <div className="mb-3 text-lg font-medium border-b-[1px] border-slate-200 pb-2">
            <div>
              <UserOutlined className="mr-3" />
              <span>Personal Information</span>
            </div>
          </div>

          {/* Phone verification section */}
          <Form.Item
            name="phone_number"
            label="Phone Number"
            rules={[
              { required: true, message: "Please enter your phone number" },
              { pattern: /^(\+\d{1,3})?\s?\d{10}$/, message: "Please enter a valid phone number" },
            ]}
          >
            <div className="flex items-center gap-2">
              <Input
                prefix={<PhoneOutlined />}
                placeholder="+91 XXXXX XXXXX"
                disabled={phoneVerified}
                style={{ width: "100%" }}
              />
              {!phoneVerified && (
                <button
                  onClick={sendOTP}
                  disabled={showOtpField && !verifyingOtp}
                  className={`${
                    showOtpField && !verifyingOtp
                      ? "cursor-not-allowed opacity-50 bg-orange hover:bg-lightOrange hover:text-orange text-white"
                      : "bg-orange hover:bg-lightOrange hover:text-orange text-white"
                  } font-medium px-2 text-nowrap py-2 rounded-md shadow`}
                  type="button"
                >
                  Verify Phone
                </button>
              )}
              {phoneVerified && <CheckCircleOutlined style={{ color: "green", fontSize: "20px" }} />}
            </div>
          </Form.Item>

          {showOtpField && !phoneVerified && (
            <Form.Item name="otp" label="Enter OTP" required>
              <div className="flex items-center gap-2">
                <Input placeholder="Enter the OTP sent to your phone" />
                <button
                  onClick={verifyOTP}
                  disabled={verifyingOtp}
                  className="bg-orange text-nowrap hover:bg-lightOrange hover:text-orange text-white font-medium px-4 py-2 rounded-md shadow"
                  type="button"
                >
                  Verify OTP
                </button>
              </div>
            </Form.Item>
          )}

          {/* Only show the rest of the form if phone is verified */}
          {phoneVerified && (
            <>
              <div className="flex gap-5 flex-wrap justify-between">
                <div className="w-[48%]">

                <Form.Item
                  name="full_name"
                  label="Full Name"
                  rules={[{ required: true, message: "Please enter your full name" }]}
                  >
                  <Input prefix={<UserOutlined />} placeholder="First and Last Name" />
                </Form.Item>
                  </div>
                  <div className="w-[48%]">

                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: "Please enter your email" },
                    { type: "email", message: "Please enter a valid email" },
                  ]}
                  >
                  <Input prefix={<MailOutlined />} placeholder="example@email.com" />
                </Form.Item>
                  </div>
              </div>

              {/* Course Selection Section */}
              <div className="mb-3 text-lg font-medium border-b-[1px] border-slate-200 pb-2">
                <div>
                  <BookOutlined className="mr-3" />
                  <span>Course Selection</span>
                </div>
              </div>
              <div className="">
                <Form.Item
                  name="course_of_interest"
                  label="Course of Interest"
                  rules={[{ required: true, message: "Please select a course" }]}
                >
                  <Select placeholder="Select a course">
                    <Option value="digital-marketing">Digital Marketing Course</Option>
                    <Option value="wordpress-shopify">Web Development with WordPress & Shopify</Option>
                    <Option value="php">Web Development with PHP</Option>
                    <Option value="python">Web Development with Python</Option>
                    <Option value="react-next">Web Development with ReactJS & NextJS</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="preferred_batch"
                  label="Preferred Batch"
                  rules={[{ required: true, message: "Please select your preferred batch" }]}
                >
                  <Radio.Group>
                    <Radio value="Weekdays">Weekdays</Radio>
                    <Radio value="Weekend">Weekend</Radio>
                  </Radio.Group>
                </Form.Item>
              </div>

              {/* Educational Background Section */}
              <div className="mb-3 text-lg font-medium border-b-[1px] border-slate-200 pb-2">
                <div>
                  <BankOutlined className="mr-3" />
                  <span>Educational Background</span>
                </div>
              </div>
              <div className="flex flex-wrap gap-4">
                <Form.Item
                  name="heighest_level_of_education"
                  label="Highest Level of Education"
                  rules={[{ required: true, message: "Please select your education level" }]}
                >
                  <Select placeholder="Select your highest education">
                    <Option value="high-school">High School</Option>
                    <Option value="undergraduate">Undergraduate</Option>
                    <Option value="graduate">Graduate</Option>
                    <Option value="postgraduate">Postgraduate</Option>
                  </Select>
                </Form.Item>
                <Form.Item name="current_employment_status" label="Current Employment Status">
                  <Select>
                    <Option value="employed">Employed</Option>
                    <Option value="self-employed">Self-employed</Option>
                    <Option value="unemployed">Unemployed</Option>
                    <Option value="student">Student</Option>
                  </Select>
                </Form.Item>
                <Form.Item
                  name="field_of_study"
                  label="Field of Study"
                  rules={[
                    { required: true, message: "Please enter your field of study" },
                    { min: 3, message: "Field of study must be at least 3 characters" },
                  ]}
                >
                  <Input placeholder="e.g., Computer Science, Business, Arts, etc." />
                </Form.Item>
              </div>

              {/* Experience and Motivation Section */}
              <div className="mb-3 text-lg font-medium border-b-[1px] border-slate-200 pb-2">
                <div>
                  <CalendarOutlined className="mr-3" />
                  <span>Experience and Motivation</span>
                </div>
              </div>
              <Form.Item
                name="motivation"
                label="Why are you interested in this course?"
                rules={[{ min: 10, message: "Please provide at least 10 characters about your motivation" }]}
              >
                <TextArea rows={4} placeholder="Tell us about your interest in this course..." />
              </Form.Item>

              <Form.Item>
                <button
                  type="submit"
                  className="w-full py-3 px-4 bg-orange text-white font-bold rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center"
                >
                  <span className="mr-2">Enroll Now</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </Form.Item>
            </>
          )}
        </Form>
      </Spin>
    </Modal>
  )
}

export default EnrollmentFormModal
